import React, { useEffect, useState } from "react";
import { getRecoveryStats, getRecoveryTrends, getRecoverySequences, getRecoveryActivity } from "../api";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useView } from "../contexts/ViewContext";

const fmtDollar = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (v) => Number(v || 0).toLocaleString();
const fmtPct = (v) => Number(v || 0).toFixed(1) + "%";

const STATUS_COLORS = {
  pending:      { bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400" },
  sent:         { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-400" },
  delivered:    { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-400" },
  opened:       { bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-400" },
  clicked:      { bg: "bg-orange-100 dark:bg-orange-900/40", text: "text-orange-700 dark:text-orange-400" },
  converted:    { bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-400" },
  bounced:      { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-400" },
  failed:       { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-400" },
};

function relativeTime(dateStr) {
  if (!dateStr) return "—";
  const now = new Date();
  const dt = new Date(dateStr);
  const diffMs = now - dt;
  if (diffMs < 0) return dt.toLocaleDateString();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(diffMs / 3600000);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(diffMs / 86400000);
  if (days < 7) return `${days}d ago`;
  return dt.toLocaleDateString();
}

export default function RecoveryPanel({ open, onClose, clientId, clientName }) {
  // 2026-08-13 — use the global date range from ViewContext instead of a local
  // duplicate. Recovery is a sub-panel of the Bookings page, so it follows the
  // same global date range. No local DateRangePicker — the header picker controls it.
  const { rangeStart, rangeEnd } = useView();
  const [tab, setTab] = useState("overview");

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [sequences, setSequences] = useState([]);
  const [sequencesLoading, setSequencesLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityPag, setActivityPag] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });

  useEffect(() => {
    if (!open || !clientId) return;
    const load = async () => {
      setStatsLoading(true); setTrendsLoading(true); setSequencesLoading(true); setActivityLoading(true);
      try {
        const [sr, tr, sq, ar] = await Promise.all([
          getRecoveryStats(clientId, rangeStart, rangeEnd),
          getRecoveryTrends(clientId, rangeStart, rangeEnd),
          getRecoverySequences(clientId, rangeStart, rangeEnd),
          getRecoveryActivity(clientId, 1, 20, rangeStart, rangeEnd),
        ]);
        setStats(sr?.data || null);
        setTrends(tr?.data?.trends || []);
        setSequences(sq?.data?.sequences || []);
        setActivity(ar?.data?.actions || []);
        setActivityPag((p) => ({ ...p, ...ar?.data?.pagination }));
      } catch (e) { console.error("Recovery load error:", e); }
      finally { setStatsLoading(false); setTrendsLoading(false); setSequencesLoading(false); setActivityLoading(false); }
    };
    load();
  }, [open, clientId, rangeStart, rangeEnd]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const loadActivityPage = async (page) => {
    setActivityLoading(true);
    try {
      const resp = await getRecoveryActivity(clientId, page, 20, rangeStart, rangeEnd);
      setActivity(resp?.data?.actions || []);
      setActivityPag((p) => ({ ...p, ...resp?.data?.pagination }));
    } catch (_) {}
    finally { setActivityLoading(false); }
  };

  const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Week of {d.week}</div>
        <div className="text-gray-600 dark:text-gray-400">{d.emails_sent} sent · {d.conversions} converted</div>
        <div className="text-emerald-600 dark:text-emerald-400">{fmtDollar(d.revenue_recovered)} recovered</div>
      </div>
    );
  };

  return (
    <>
      <div onClick={onClose} className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />

      <div className={`fixed top-0 right-0 h-full w-[860px] max-w-[calc(100vw-60px)] z-50 bg-white dark:bg-gray-900 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Recovery Dashboard</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{clientName} — Email recovery performance</p>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {[
                { key: "overview", label: "Overview" },
                { key: "activity", label: `Activity${activity.length ? ` (${activityPag.total || activity.length})` : ""}` },
              ].map(({ key, label }) => (
                <button key={key} type="button" onClick={() => setTab(key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === key ? "bg-[#32B1FF] text-white" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {tab === "overview" && (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-5 gap-3">
                {[
                  { label: "Recovered Revenue", value: statsLoading ? "..." : fmtDollar(stats?.revenue_recovered), color: "border-t-emerald-500" },
                  { label: "Sent", value: statsLoading ? "..." : fmtNum(stats?.total_sent), color: "border-t-[#32B1FF]" },
                  { label: "Opened", value: statsLoading ? "..." : fmtNum(stats?.opened), sub: statsLoading ? "" : fmtPct(stats?.open_rate), color: "border-t-amber-400" },
                  { label: "Clicked", value: statsLoading ? "..." : fmtNum(stats?.clicked), sub: statsLoading ? "" : fmtPct(stats?.click_rate), color: "border-t-orange-400" },
                  { label: "Converted", value: statsLoading ? "..." : fmtNum(stats?.converted), sub: statsLoading ? "" : fmtPct(stats?.conversion_rate), color: "border-t-emerald-400" },
                ].map((k) => (
                  <div key={k.label} className={`bg-gray-50 dark:bg-gray-800/50 rounded-lg border-t-4 ${k.color} p-3`}>
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{k.value}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{k.label}</div>
                    {k.sub && <div className="text-sm text-gray-400 dark:text-gray-500">{k.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Two-column: Funnel + Chart */}
              <div className="grid grid-cols-2 gap-5">
                {/* Funnel */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Email Funnel</h3>
                  {statsLoading ? <div className="animate-pulse h-32 bg-gray-200 dark:bg-gray-700 rounded" /> : !stats?.total_sent ? (
                    <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">No emails sent yet.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {[
                        { stage: "Sent", count: stats.total_sent },
                        { stage: "Delivered", count: stats.delivered },
                        { stage: "Opened", count: stats.opened },
                        { stage: "Clicked", count: stats.clicked },
                        { stage: "Converted", count: stats.converted },
                      ].map((s, i, arr) => {
                        const max = arr[0].count || 1;
                        const pct = (s.count / max) * 100;
                        const isLast = i === arr.length - 1;
                        return (
                          <div key={s.stage} className="flex items-center gap-2">
                            <span className="w-16 text-right text-sm text-gray-500 dark:text-gray-400 shrink-0">{s.stage}</span>
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-5 overflow-hidden">
                              <div className={`h-full rounded-full ${isLast ? "bg-emerald-500" : "bg-[#32B1FF]"} flex items-center justify-end pr-1.5`}
                                style={{ width: `${Math.max(pct, 3)}%` }}>
                                {pct > 20 && <span className="text-sm font-semibold text-white">{s.count}</span>}
                              </div>
                            </div>
                            {pct <= 20 && <span className="text-sm text-gray-500 w-8 shrink-0">{s.count}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Revenue chart */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Revenue Over Time</h3>
                  {trendsLoading ? <div className="animate-pulse h-32 bg-gray-200 dark:bg-gray-700 rounded" /> : !trends.length ? (
                    <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">No trend data yet.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={180}>
                      <ComposedChart data={trends} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
                        <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#9CA3AF" }} tickLine={false} axisLine={false} />
                        <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar yAxisId="left" dataKey="emails_sent" fill="rgba(50,177,255,0.3)" radius={[3, 3, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="revenue_recovered" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 2 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Sequence table */}
              {sequences.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Sequence Performance</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="py-2 px-2 text-left text-gray-500 dark:text-gray-400 font-semibold">#</th>
                        <th className="py-2 px-2 text-left text-gray-500 dark:text-gray-400 font-semibold">Campaign</th>
                        <th className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-semibold">Sent</th>
                        <th className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-semibold">Opened</th>
                        <th className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-semibold">Converted</th>
                        <th className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-semibold">Revenue</th>
                        <th className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-semibold">Conv %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sequences.map((s) => (
                        <tr key={s.sequence_number} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{s.sequence_number}</td>
                          <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{s.campaign_name}</td>
                          <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400">{fmtNum(s.sent)}</td>
                          <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400">{fmtNum(s.opened)}</td>
                          <td className="py-2 px-2 text-right font-semibold text-emerald-600 dark:text-emerald-400">{fmtNum(s.converted)}</td>
                          <td className="py-2 px-2 text-right text-emerald-600 dark:text-emerald-400">{fmtDollar(s.revenue_recovered)}</td>
                          <td className="py-2 px-2 text-right font-semibold text-gray-700 dark:text-gray-300">{fmtPct(s.conversion_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {tab === "activity" && (
            <>
              {activityLoading ? (
                <div className="animate-pulse space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 bg-gray-200 dark:bg-gray-800 rounded" />)}</div>
              ) : !activity.length ? (
                <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No recovery activity yet.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="py-2 px-2 text-left font-semibold text-gray-500 dark:text-gray-400">Recipient</th>
                          <th className="py-2 px-2 text-left font-semibold text-gray-500 dark:text-gray-400">Subject</th>
                          <th className="py-2 px-2 text-left font-semibold text-gray-500 dark:text-gray-400">Type</th>
                          <th className="py-2 px-2 text-right font-semibold text-gray-500 dark:text-gray-400">Value</th>
                          <th className="py-2 px-2 text-center font-semibold text-gray-500 dark:text-gray-400">Status</th>
                          <th className="py-2 px-2 text-right font-semibold text-gray-500 dark:text-gray-400">Sent</th>
                          <th className="py-2 px-2 text-left font-semibold text-gray-500 dark:text-gray-400">By</th>
                          <th className="py-2 px-2 text-right font-semibold text-gray-500 dark:text-gray-400">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activity.map((a) => {
                          const sc = STATUS_COLORS[a.status] || STATUS_COLORS.pending;
                          return (
                            <tr key={a.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-2 px-2">
                                <div className="font-medium text-gray-700 dark:text-gray-300 truncate max-w-[120px]">{a.to_name || "—"}</div>
                                <div className="text-gray-500 dark:text-gray-400 truncate max-w-[120px]">{a.to_email}</div>
                              </td>
                              <td className="py-2 px-2 text-gray-600 dark:text-gray-400 truncate max-w-[160px]">{a.subject || "—"}</td>
                              <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{a.guest_type?.replace(/_/g, " ") || "—"}</td>
                              <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400">{a.cart_value ? fmtDollar(a.cart_value) : "—"}</td>
                              <td className="py-2 px-2 text-center">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-sm font-semibold ${sc.bg} ${sc.text}`}>{a.status}</span>
                              </td>
                              <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400">{relativeTime(a.sent_at)}</td>
                              <td className="py-2 px-2 text-gray-500 dark:text-gray-400 truncate max-w-[80px]">{a.created_by || "auto"}</td>
                              <td className="py-2 px-2 text-right font-semibold text-emerald-600 dark:text-emerald-400">{a.converted_value ? fmtDollar(a.converted_value) : "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {activityPag.total_pages > 1 && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Page {activityPag.page} of {activityPag.total_pages}</span>
                      <div className="flex gap-2">
                        <button disabled={activityPag.page <= 1} onClick={() => loadActivityPage(activityPag.page - 1)}
                          className="px-2.5 py-1.5 text-sm rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-40">Previous</button>
                        <button disabled={activityPag.page >= activityPag.total_pages} onClick={() => loadActivityPage(activityPag.page + 1)}
                          className="px-2.5 py-1.5 text-sm rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-40">Next</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
